import { getFormErrorMessage } from "@core/utility/get-error-message";
import { TierLevelFormRule, TierLevelFormSchema } from "@factory/form/form.rule";
import { useFormDefaultTierLevel } from "@factory/form/form-default-tier-level.hook";
import { ActionEnum } from "@factory/model/actions.enum";
import { GetResponseTierLevelModel } from "@factory/model/query-model-tier-level";
import { 
  useQyDeleteTierLevel, 
  useQyGetTierLevelById, 
  useQyUpdateTierLevel} from "@factory/query/tier-level.query";
import { useFormApiMapper } from "@factory/services/form-api-mapper.hook";
import { zodResolver } from "@hookform/resolvers/zod";
import { ButtonAction } from "@shared/ui/header-button/button-action";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";
import { confirmDialog } from "primereact/confirmdialog";
import { FieldErrors, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

export function useEditTierLevel() {
  // LOCAL DECLARATION
  const { formatDate } = useDateTimeContext();
  const { getDefault } = useFormDefaultTierLevel();
  const navigate = useNavigate();
  const { mapUpdateTierLevel, mapDeleteTierLevel } = useFormApiMapper();
  const { showError, showSuccess } = useNotificationContext();
  const { tierLevelId } = useParams();
  const actions: ButtonAction[] = [
    { label: "Update", type: "split", value: ActionEnum.Update },
    { label: "Delete", type: "more", value: ActionEnum.Delete },
  ];

  // QUERY DECLARATION
  // API GETBYID TierLevel
  const handleGetApiSuccess = (data: GetResponseTierLevelModel) => {
    if (!data || data.data?.length === 0) {
      return;
    }

    const responseData = data.data?.[0];

      setValue("tenant", responseData?.tenant || "");
      setValue("name", responseData?.name || "");
      setValue("level", responseData?.level);
      setValue("minAmount", responseData?.min_amount);
      setValue("maxAmount", responseData?.max_amount);
      setValue("description", responseData?.description || "");
  };
  const {
    data: tierLevelResponse,
    isLoading,
    isError: tierLevelError,
  } = useQyGetTierLevelById(tierLevelId || "", handleGetApiSuccess);
  const tierLevelData = tierLevelResponse?.data?.[0];

  // API UPDATE TierLevel
  const handleUpdateApiSuccess = () => {
    showSuccess("TierLevel updated successfully");
  };
  const { mutate: updateTierLevel, isLoading: isEditLoading } = useQyUpdateTierLevel(
    handleUpdateApiSuccess
  );

  // API DELETE TierLevel
  const handleDeleteApiSuccess = () => {
    handleBack();
    showSuccess("TierLevel deleted successfully");
  };
  const { mutate: deleteTierLevel } = useQyDeleteTierLevel(
    handleDeleteApiSuccess
  );

  // LOCAL FUNCTIONS
  const formMethod = useForm<TierLevelFormSchema>({
    defaultValues: getDefault(tierLevelData),
    resolver: zodResolver(TierLevelFormRule),
  });
  const { handleSubmit, setValue, watch, getValues } = formMethod;
  const handleValidate = (form: TierLevelFormSchema) => {
    if (!tierLevelData || !tierLevelData.code) {
      throw new Error("No tierLevel data");
    }

    const formData = mapUpdateTierLevel(form, tierLevelData.code);
    updateTierLevel(formData);
  };
  const handleValidateError = (err: FieldErrors<TierLevelFormSchema>) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };
  const handleBack = () => {
    navigate("../");
  };
  const accept = () => {
    if (!tierLevelData || !tierLevelData.code) {
      throw new Error("No tierLevel data found");
    }

    const form = mapDeleteTierLevel(tierLevelData.code);
    deleteTierLevel(form);
  };
  const handleAction = (action: ButtonAction) => {
    switch (action.value) {
      case ActionEnum.Update:
        handleSubmit(handleValidate, handleValidateError)();
        break;
      
      case ActionEnum.Delete:
        confirmDialog({
          message: "Are you sure you want to delete this record?",
          header: "Delete Confirmation",
          icon: "pi pi-info-circle",
          defaultFocus: "reject",
          acceptClassName: "p-button-danger",
          accept,
        });
        break;
    }
  };

   return {
    tierLevelId,
    tierLevelData,
    isLoading,
    tierLevelError,
    formMethod,
    actions,
    isEditLoading,
    handleSubmit,
    setValue,
    watch,
    getValues,
    handleBack,
    handleAction,
  };
}